import { VideoRequestSummaryModel } from "./../api/VideoRequest/models/VideoRequestSummaryModel";
import { listVideoRequests } from "./../api/VideoRequest/listVideoRequests";
import { computedFn } from "mobx-utils";
import { truckIcon } from "@/assets/icons/TruckIcons";
import { commercialTruckIcon } from "@/assets/icons/CommercialTruckIcons";
import { ListMapRequestsRequest, listMapRequests, RequestModel, requestDetails } from "@/api/Requests";
import { listCollectionEvents, ListCollectionEventsRequest, CollectionEventSummaryModel } from "@/api/CollectionEvents";
import { listCollectionIndexes, ListCollectionIndexesRequest, CollectionIndexModel } from "@/api/CollectionEvents";
import { getAddressSuggestions, GetAddressSuggestionsRequest, AddressSuggestionModel, AddressGeocodeModel } from "@/api/AddressLookup";
import { ListTruckLocationsRequest, listTruckLocations, TruckLocationModel } from "@/api/TruckLocations";
import { CouncilConfigurationModel, WasteConfigurationModel, MapBoundsModel, MapProvider } from "@/api/CouncilConfiguration";
import { action, observable, computed, reaction } from "mobx";
import L, { latLng, LatLng, LatLngBoundsExpression, LatLngBounds } from "leaflet";
import add from "date-fns/add";
import format from "date-fns/format";
import isSameDay from "date-fns/isSameDay";
import parse from "date-fns/parse";
import userStore from "./userStore";
import appSettings from "@/plumbing/ApplicationSettings";
import remove from "lodash/remove";
import { DATE_FORMAT } from "@/Constants";
import { LoadingStore } from "./loadingStore";
import notificationStore from "./notificationStore";
import { ListVideoRequestsRequest } from "@/api/VideoRequest";
import axios, { CancelTokenSource } from "axios";

type WasteConfigFilter = {
  isCollectionsVisible: boolean;
  isIssuesVisible: boolean;
};
export class MapStore {
  @observable previuosCollectionEvents: CollectionEventSummaryModel[] = [];
  @observable collectionEvents: CollectionEventSummaryModel[] = [];
  @observable collectionIndexes: CollectionIndexModel[] = [];
  @observable loading = new LoadingStore();
  @observable initialCenter: LatLng = latLng(-27.3505823, 152.8997586);
  @observable center: LatLng = latLng(-27.3505823, 152.8997586);
  @observable configMapBounds: MapBoundsModel | undefined = undefined;
  @observable maxBounds: LatLngBoundsExpression | undefined = undefined;
  @observable initialZoom = 12;
  @observable zoom = 12;
  @observable minZoom = 10;
  @observable maxZoom = 19;
  @observable isSatelliteView = false;
  @observable date = format(new Date(), "yyyy-MM-dd");
  @observable userSelectedAddress: AddressSuggestionModel | null = null;
  @observable userTypedAddress = "";
  @observable addressSuggestions: AddressSuggestionModel[] = [];
  @observable userSelectedAddressGeocode: AddressGeocodeModel | null = null;
  @observable userSelectedAddressLastCollectionEventDate: string | null = null;
  @observable wasteConfiguration: (WasteConfigurationModel & WasteConfigFilter)[] = [];
  @observable collectionEventColours: { [key: string]: string } = {};
  @observable collectionIssueIcons: { [key: string]: L.Icon } = {};
  @observable collectionEventIcons: { [key: string]: L.Icon } = {};
  @observable isLgaLayerVisible = false;
  @observable isPropertyBoundaryLayerVisible = false;
  @observable bounds?: LatLngBounds | H.geo.Rect;
  @observable previousBounds?: H.geo.Rect;
  @observable isTrucksVisible = true;
  @observable truckLocations: TruckLocationModel[] = [];
  @observable selectedCollectionEvent: CollectionEventSummaryModel | null = null;
  @observable isCollectionEventPopupOpen = false;
  @observable minPropertyBoundaryZoom = 17;
  @observable requests: RequestModel[] = [];
  @observable isRequestsVisible = true;
  @observable isRequestPopupOpen = false;
  @observable selectedRequest: RequestModel | null = null;
  @observable showSearchBounds = false;
  @observable isDesktopFilterExpanded = true;
  @observable isMobileFilterExpanded = false;
  @observable activeCouncil: string | null = null;
  @observable councilCountry: string | null = null;
  @observable videoRequests: VideoRequestSummaryModel[] = [];
  @observable isVideoRequestsVisible = true;
  @observable selectedVideoRequest: VideoRequestSummaryModel | null = null;
  @observable cancellationTokenSource: CancelTokenSource | null = null;
  @observable collectionIssueHereIcons: { [key: string]: HTMLElement } = {};
  @observable collectionEventHereIcons: { [key: string]: HTMLElement } = {};
  @observable isSelectedAddressPopupOpen = false;
  @observable isTruckSummaryPopupOpen = false;
  @observable selectedTruck: TruckLocationModel | null = null;
  @observable isVideoRequestsPopupOpen = true;
  @observable mapProvider: MapProvider = MapProvider.HereRaster;

  @computed get resolution() {
    return Math.round(this.zoom / 2);
  }

  @computed get formattedDate() {
    return this.date ? format(new Date(this.date), DATE_FORMAT) : "";
  }

  @computed get isCollectionDateMoreThan6MonthsAgo() {
    return new Date(this.date) < add(new Date(), { months: -6 });
  }

  @computed get isToday() {
    const mapDate = parse(this.date, "yyyy-MM-dd", new Date());
    const today = new Date();

    return isSameDay(mapDate, today);
  }

  @computed get isHereVectorMap() {
    return this.mapProvider == MapProvider.HereVector;
  }

  isInBounds(latitude: number, longitude: number): boolean {
    return this.bounds
      ? this.bounds instanceof H.geo.Rect
        ? this.bounds.containsPoint(new window.H.geo.Point(latitude, longitude))
        : this.bounds?.contains(latLng(latitude, longitude))
      : false;
  }
  @action.bound async fetchCollectionEvents() {
    if (!userStore.activeCouncil) return;
    return this.loading.track(async () => {
      try {
        const council = userStore.activeCouncil;
        const eventDate = this.date;
        const boundary = this.bounds
          ? this.bounds instanceof H.geo.Rect
            ? [this.bounds.getRight(), this.bounds.getTop(), this.bounds.getLeft(), this.bounds.getBottom()]
            : [this.bounds.getEast(), this.bounds.getNorth(), this.bounds.getWest(), this.bounds.getSouth()]
          : undefined;
        const request: ListCollectionEventsRequest = {
          eventDate: new Date(this.date),
          boundary
        };
        const timestamp = new Date();
        console.debug(`Fetching collection events for ${council} ${eventDate}`, timestamp);
        const collectionEventsResponse = await listCollectionEvents(request);
        console.debug(
          `Fetched ${collectionEventsResponse.collectionEvents.length} collection events for ${council} ${eventDate}`,
          Math.abs(new Date().getMilliseconds() - timestamp.getMilliseconds())
        );
        if (council !== userStore.activeCouncil || eventDate !== this.date) {
          console.debug(`Ignoring collection events response for ${council} ${eventDate} as council and/or date has changed`);
          return;
        }
        this.previuosCollectionEvents = this.collectionEvents;
        this.collectionEvents = collectionEventsResponse.collectionEvents;
      } catch (error) {
        notificationStore.error("An unexpected error occurred while loading collection events");
      }
    }, "loadMap");
  }

  @action.bound async fetchCollectionIndexes() {
    if (!userStore.activeCouncil) return;
    try {
      const council = userStore.activeCouncil;
      const eventDate = this.date;
      const boundary = this.bounds
        ? this.bounds instanceof H.geo.Rect
          ? [this.bounds.getRight(), this.bounds.getTop(), this.bounds.getLeft(), this.bounds.getBottom()]
          : [this.bounds.getEast(), this.bounds.getNorth(), this.bounds.getWest(), this.bounds.getSouth()]
        : [(this.maxBounds as number[][])[1][1], (this.maxBounds as number[][])[0][0], (this.maxBounds as number[][])[0][1], (this.maxBounds as number[][])[1][0]];

      const request: ListCollectionIndexesRequest = {
        eventDate: new Date(this.date),
        boundary,
        resolution: this.resolution
      };

      const timestamp = new Date();
      console.debug(`Fetching collection indexes for ${council} ${eventDate} [${boundary}] ${this.zoom}`);
      if (this.cancellationTokenSource) {
        this.cancellationTokenSource.cancel("Operation cancelled by the user.");
      }
      this.cancellationTokenSource = axios.CancelToken.source();
      const response = await listCollectionIndexes(request, this.cancellationTokenSource.token);
      this.cancellationTokenSource = null;
      console.debug(
        `Fetched ${response?.indexes?.length} collection indexes for ${council} ${eventDate} [${boundary}] ${this.zoom}`,
        Math.abs(new Date().getMilliseconds() - timestamp.getMilliseconds())
      );
      if (council !== userStore.activeCouncil || eventDate !== this.date) {
        console.debug(`Ignoring collection events response for ${council} ${eventDate} as council and/or date has changed`);
        return;
      }

      this.collectionIndexes = response?.indexes ?? [];
    } catch (error) {
      notificationStore.error("An unexpected error occurred while loading collection events");
    }
  }

  @action.bound async fetchTruckLocations() {
    return this.loading.track(async () => {
      try {
        const request: ListTruckLocationsRequest = { eventDate: new Date(this.date) };
        const council = userStore.activeCouncil;
        const eventDate = this.date;
        const mapDate = parse(eventDate, "yyyy-MM-dd", new Date());

        if (!isSameDay(mapDate, new Date())) {
          this.truckLocations = [];
          return;
        }

        const truckLocationsResponse = await listTruckLocations(request);
        console.debug(`Fetched ${truckLocationsResponse.truckLocations.length} truck locations for ${council} ${eventDate}`);
        if (council !== userStore.activeCouncil || eventDate !== this.date) {
          console.debug(`Ignoring truck locations response for ${council} ${eventDate} as council and/or date has changed`);
          return;
        }

        this.truckLocations = truckLocationsResponse.truckLocations;
      } catch (error) {
        notificationStore.error("An unexpected error occurred while loading truck locations");
      }
    }, "loadMap");
  }

  @action.bound async fetchRequestDetails(requestModel: RequestModel) {
    return this.loading.track(async () => {
      const requestId = requestModel.requestId;
      try {
        console.debug(`Fetched request Details for ${userStore.activeCouncil}, request id ${requestId} ${this.date}`);

        const requestWithDetailsResponse = await requestDetails({ requestId: requestId, date: new Date(this.date) });

        this.selectedRequest = requestWithDetailsResponse.request;
      } catch (error) {
        notificationStore.error(`An unexpected error occurred while loading request details id ${requestId}`);
      }
    }, "loadRequestDetails");
  }

  @action.bound async fetchRequests() {
    if (!userStore.activeCouncil) return;
    this.selectedRequest = null;
    return this.loading.track(async () => {
      try {
        const council = userStore.activeCouncil;
        const eventDate = this.date;
        const request: ListMapRequestsRequest = { date: new Date(this.date) };
        const requestsResponse = await listMapRequests(request);
        console.debug(`Fetched ${requestsResponse.requests.length} requests for ${council} ${eventDate}`);

        if (council !== userStore.activeCouncil || eventDate !== this.date) {
          console.debug(`Ignoring requests response for ${council} ${eventDate} as council and/or date has changed`);
          return;
        }

        this.requests = requestsResponse.requests;
      } catch (error) {
        notificationStore.error("An unexpected error occurred while loading truck requests");
      }
    }, "loadMap");
  }

  @action.bound async fetchVideoRequests() {
    if (!userStore.activeCouncil || !userStore.canAccessVideoRequests()) return;
    return this.loading.track(async () => {
      try {
        const council = userStore.activeCouncil;
        const videoDate = this.date;
        const request: ListVideoRequestsRequest = { videoDate: new Date(this.date) };
        const requestsResponse = await listVideoRequests(request);
        console.debug(`Fetched ${requestsResponse.videoRequests.length} video requests for ${council} ${videoDate}`);

        if (council !== userStore.activeCouncil || videoDate !== this.date) {
          console.debug(`Ignoring requests response for ${council} ${videoDate} as council and/or date has changed`);
          return;
        }

        this.videoRequests = requestsResponse.videoRequests;
      } catch (error) {
        notificationStore.error("An unexpected error occurred while loading video requests");
      }
    }, "loadMap");
  }

  @action.bound async updateTruckLocation(truckLocation: TruckLocationModel) {
    const truckDate = new Date(truckLocation.eventDateTime);
    const mapDate = parse(this.date, "yyyy-MM-dd", new Date());
    const isDayMatching = isSameDay(truckDate, mapDate);
    const isCouncilMatching = truckLocation.council === userStore.activeCouncil;

    if (!isDayMatching || !isCouncilMatching) return;

    const truck = this.truckLocations.find(t => t.truckCode === truckLocation.truckCode);
    if (truck) {
      remove(this.truckLocations, truck);
    }

    this.truckLocations.push(truckLocation);
    // Force reactivity by replacing the array reference
    this.truckLocations = [...this.truckLocations];
  }

  @action.bound loadCouncilConfiguration(configuration: CouncilConfigurationModel) {
    return this.loading.track(async () => {
      this.date = format(new Date(), "yyyy-MM-dd");

      this.activeCouncil = userStore.activeCouncil;

      const configCenter = configuration.mapCenter;
      this.initialCenter = latLng(configCenter.latitude, configCenter.longitude);

      this.configMapBounds = configuration.mapBounds;
      const bounds = configuration.mapBounds;
      this.maxBounds = [
        [bounds.topLeft.latitude, bounds.topLeft.longitude],
        [bounds.bottomRight.latitude, bounds.bottomRight.longitude]
      ];

      this.initialZoom = configuration.mapZoom;
      this.minZoom = configuration.mapMinZoom;
      this.resetZoomAndSelection();

      this.wasteConfiguration = configuration.wasteConfiguration.map(waste => ({ ...waste, isCollectionsVisible: true, isIssuesVisible: true }));

      this.collectionEventColours = configuration.wasteConfiguration.reduce((accumulator, current) => {
        return { ...accumulator, [current.id]: current.markerColour };
      }, {});

      this.collectionIssueIcons = configuration.wasteConfiguration.reduce((accumulator, current) => {
        const iconUrl = `data:image/svg+xml,${encodeURIComponent(current.collectionIssueMarker)}`;
        return {
          ...accumulator,
          [current.id]: new L.Icon({
            iconUrl: iconUrl,
            iconSize: [17, 17],
            iconAnchor: [8.5, 17]
          })
        };
      }, {});
      this.collectionEventIcons = configuration.wasteConfiguration.reduce((accumulator, current) => {
        const iconUrl = `data:image/svg+xml,${encodeURIComponent(current.collectionEventMarker)}`;
        return {
          ...accumulator,
          [current.id]: new L.Icon({
            iconUrl: iconUrl,
            iconSize: [15, 15],
            iconAnchor: [7.5, 15]
          })
        };
      }, {});

      this.userSelectedAddress = null;
      this.userTypedAddress = "";

      this.collectionIssueHereIcons =
        configuration.wasteConfiguration.reduce((accumulator, current) => {
          const domElement = this.createDomElementIconWithSvg(current.collectionIssueMarker, "issue-icon", "15");
          domElement.style.marginLeft = "-7.5px";
          return {
            ...accumulator,
            [current.id]: domElement
          };
        }, {}) ?? {};

      this.collectionEventHereIcons =
        configuration.wasteConfiguration.reduce((accumulator, current) => {
          const domElement = this.createDomElementIconWithSvg(current.collectionEventMarker, "event-icon", "15");
          domElement.style.marginLeft = "-7.5px";
          return {
            ...accumulator,
            [current.id]: domElement
          };
        }, {}) ?? {};

      this.councilCountry = configuration.country;

      this.mapProvider = configuration.mapProvider;
    }, "loadMap");
  }

  @computed get lgaBaseUrl() {
    return `${appSettings.spectrumProxyBaseUrl}/rest/Spatial/WMS?access_token=${userStore.user?.access_token}`;
  }

  @computed get propertyBoundaryBaseUrl() {
    return `${appSettings.spectrumProxyBaseUrl}/rest/Spatial/MapTilingService/${userStore.councilConfiguration?.propertyBoundaryMapName}/{z}/{x}:{y}/tile.png?access_token=${userStore.user?.access_token}`;
  }

  createDomElementIconWithSvg(iconSvg: string | undefined, alt: string, size: string): HTMLElement {
    const iconUrl = `data:image/svg+xml,${encodeURIComponent(iconSvg || "")}`;
    const domElement = this.createDomElementIcon(iconUrl, alt, size);
    domElement.style.userSelect = "none";
    domElement.setAttribute("unselectable", "on");
    return domElement;
  }

  createDomElementIcon(iconUrl: string, alt: string, size: string): HTMLElement {
    const domElement = document.createElement("div");
    domElement.style.width = `${size}px`; // Set the width of the icon
    domElement.style.height = `${size}px`; // Set the height of the icon
    domElement.style.display = "flex";
    domElement.style.alignItems = "center";
    domElement.style.justifyContent = "center";
    domElement.style.cursor = "pointer";
    domElement.innerHTML = `<img src="${iconUrl}" alt="${alt}" style="width: 100%; height: 100%;" />`;
    domElement.style.userSelect = "none";
    domElement.setAttribute("unselectable", "on");

    return domElement;
  }

  getTruckIcon = computedFn((truck: TruckLocationModel) => {
    const isDimmed = this.selectedRequest && this.selectedRequest.tasks.some(t => !!t.truckCode) && this.selectedRequest.tasks.every(t => t.truckCode !== truck.truckCode);
    // Check both isCommercial and IsCommercial for compatibility
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isCommercial = (truck as any).isCommercial === true || (truck as any).IsCommercial === true;
    const iconHtml = isCommercial ? commercialTruckIcon(truck.wasteStreamId, truck.truckCode) : truckIcon(truck.wasteStreamId, truck.truckCode);
    const icon = new L.DivIcon({
      html: iconHtml,
      shadowSize: [0, 0],
      iconSize: [60, 60],
      className: isDimmed ? "truck-icon-dimmed" : "truck-icon",
      iconAnchor: [30, 60]
    });
    return icon;
  });

  resetZoomAndSelection = () => {
    this.zoom = this.initialZoom;
    this.userSelectedAddressGeocode = null;
    this.bounds = undefined;
  };

  @action.bound async fetchAddressSuggestions(userTypedAddress: string) {
    return this.loading.track(async () => {
      try {
        if (!userStore.councilConfiguration) return;
        if (this.userSelectedAddress?.addressLine1 && userTypedAddress.startsWith(this.userSelectedAddress?.addressLine1)) return;

        this.addressSuggestions = [];
        const request: GetAddressSuggestionsRequest = {
          query: userTypedAddress,
          originLatitude: userStore.councilConfiguration.searchOrigin.latitude,
          originLongitude: userStore.councilConfiguration.searchOrigin.longitude,
          searchRadiusKm: userStore.councilConfiguration.searchRadiusKm
        };
        const addressSuggestionsResponse = await getAddressSuggestions(request);
        if (this.userTypedAddress.trim() === userTypedAddress) {
          this.addressSuggestions = addressSuggestionsResponse.addressSuggestions;
        }
      } catch (error) {
        notificationStore.error("An unexpected error occurred while performing the address search");
      }
    }, "fetchAddressSuggestions");
  }

  @action.bound async clearAddressSuggestions() {
    this.addressSuggestions = [];
  }

  isEventVisible(event: CollectionEventSummaryModel) {
    const wasteConfig = this.wasteConfiguration.find(w => w.id === event.wasteStreamId);
    if (!wasteConfig) return false;
    if (event.isCollectionIssue) return wasteConfig.isIssuesVisible;
    return wasteConfig.isCollectionsVisible;
  }
}

const mapStore = new MapStore();

// Due to dependency resolution order, userStore may not initially be set
setTimeout(
  () =>
    reaction(
      () => userStore.councilConfiguration,
      () => {
        if (userStore.councilConfiguration) mapStore.loadCouncilConfiguration(userStore.councilConfiguration);
      },
      { fireImmediately: true }
    ),
  1
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).mapStore = mapStore;

export default mapStore;
